/**
 * Custom hooks for Reference Data (Enums) API
 *
 * Provides hooks for fetching enum values for dropdowns:
 * - Shipment Status
 * - Cargo Type
 * - Transport Mode
 * - Container Type
 * - Container Size
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface EnumValueItem {
  value: string;
  label: string;
  description: string | null;
}

export interface EnumValuesResponse {
  enum_name: string;
  default_value: string | null;
  values: EnumValueItem[];
}

export type EnumName =
  | "shipment_status"
  | "container_physical_status"
  | "booking_status"
  | "cargo_type"
  | "transport_mode"
  | "container_type"
  | "container_size";

// ============================================================================
// QUERY KEYS
// ============================================================================

export const ENUMS_QUERY_KEYS = {
  all: ["enums"] as const,
  byName: (enumName: string) => [...ENUMS_QUERY_KEYS.all, enumName] as const,
  list: () => [...ENUMS_QUERY_KEYS.all, "list"] as const,
};

// ============================================================================
// GET ENUM VALUES HOOK
// ============================================================================

/**
 * Hook to fetch values for a specific enum
 *
 * @param enumName - Name of the enum to fetch
 * @param enabled - Whether the query should be enabled
 *
 * Supported enums:
 * - shipment_status: Container lifecycle statuses (default: draft)
 * - cargo_type: FCL, LCL, air_freight, etc. (default: FCL)
 * - transport_mode: sea, air, road, rail, multimodal (default: sea)
 * - container_type: dry, reefer, tank, open_top, flat_rack (default: dry)
 * - container_size: 20, 40, 40HC, 45HC (default: 40)
 */
export function useEnumValues(enumName: EnumName, enabled: boolean = true) {
  return useQuery({
    queryKey: ENUMS_QUERY_KEYS.byName(enumName),
    queryFn: async (): Promise<EnumValuesResponse> => {
      return await apiGet<EnumValuesResponse>(`/enums/${enumName}`);
    },
    enabled,
    staleTime: Infinity, // Enums are static
  });
}

// ============================================================================
// LIST AVAILABLE ENUMS HOOK
// ============================================================================

/**
 * Hook to list all available enum names
 */
export function useAvailableEnums(enabled: boolean = true) {
  return useQuery({
    queryKey: ENUMS_QUERY_KEYS.list(),
    queryFn: async (): Promise<string[]> => {
      return await apiGet<string[]>("/enums");
    },
    enabled,
    staleTime: Infinity,
  });
}

// ============================================================================
// CONVENIENCE HOOKS FOR SPECIFIC ENUMS
// ============================================================================

/**
 * Hook to fetch shipment status options (business lifecycle)
 * Default: DRAFT
 * @deprecated Use useContainerPhysicalStatuses for physical tracking
 */
export function useShipmentStatuses(enabled: boolean = true) {
  return useEnumValues("shipment_status", enabled);
}

/**
 * Hook to fetch container physical status options (2025 model)
 * Tracks actual physical container state: PENDING, GATE_IN, IN_TRANSIT, DELIVERED, etc.
 * Default: PENDING
 */
export function useContainerPhysicalStatuses(enabled: boolean = true) {
  return useEnumValues("container_physical_status", enabled);
}

/**
 * Hook to fetch booking status options
 * Default: DRAFT
 */
export function useBookingStatuses(enabled: boolean = true) {
  return useEnumValues("booking_status", enabled);
}

/**
 * Hook to fetch cargo type options
 * Default: FCL
 */
export function useCargoTypes(enabled: boolean = true) {
  return useEnumValues("cargo_type", enabled);
}

/**
 * Hook to fetch transport mode options
 * Default: sea
 */
export function useTransportModes(enabled: boolean = true) {
  return useEnumValues("transport_mode", enabled);
}

/**
 * Hook to fetch container type options
 * Default: dry
 */
export function useContainerTypes(enabled: boolean = true) {
  return useEnumValues("container_type", enabled);
}

/**
 * Hook to fetch container size options
 * Default: 40
 */
export function useContainerSizes(enabled: boolean = true) {
  return useEnumValues("container_size", enabled);
}
